import React from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  SxProps,
  Theme,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import QRCode from "qrcode";

interface LinkDisplayProps {
  url?: string;
  label?: string;
  openUrl?: string;
  openLabel?: string;
  showCopyButton?: boolean;
  showText?: boolean;
  showOpenButton?: boolean;
  showQrDownloadButton?: boolean;
  qrFileName?: string;
  withBackground?: boolean;
  sx?: SxProps<Theme>;
}

const LinkDisplay: React.FC<LinkDisplayProps> = ({
  url,
  label,
  openUrl,
  openLabel = "Link",
  showCopyButton = true,
  showText = true,
  showOpenButton = true,
  showQrDownloadButton = false,
  qrFileName,
  withBackground = false,
  sx,
}) => {
  const [copied, setCopied] = React.useState(false);
  const [downloadingQr, setDownloadingQr] = React.useState(false);

  const resolvedOpenUrl = openUrl || url;
  const displayText = label || url;

  const copyTextToClipboard = async (text: string) => {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    textArea.setAttribute("readonly", "");

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand("copy");

    document.body.removeChild(textArea);

    if (!successful) {
      throw new Error("Fallback copy command failed");
    }
  };

  const handleCopy = async () => {
    if (!url) return;

    try {
      await copyTextToClipboard(url);
      setCopied(true);

      window.setTimeout(() => {
        setCopied(false);
      }, 1400);
    } catch (error) {
      console.error("Failed to copy link", error);
    }
  };

  const createQrFileName = () => {
    if (qrFileName) {
      return qrFileName.endsWith(".png")
        ? qrFileName
        : `${qrFileName}.png`;
    }

    const safeLabel = (openLabel || "link")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    return `${safeLabel || "link"}-qr-code.png`;
  };

  const handleQrDownload = async () => {
    if (!resolvedOpenUrl || downloadingQr) return;

    try {
      setDownloadingQr(true);

      const qrDataUrl = await QRCode.toDataURL(resolvedOpenUrl, {
        type: "image/png",
        width: 1024,
        margin: 4,
        errorCorrectionLevel: "H",
        color: {
          dark: "#000000",
          light: "#ffffff",
        },
      });

      const downloadLink = document.createElement("a");

      downloadLink.href = qrDataUrl;
      downloadLink.download = createQrFileName();

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      console.error("Failed to generate QR code", error);
    } finally {
      setDownloadingQr(false);
    }
  };

  if (!url && !displayText && !resolvedOpenUrl) return null;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        maxWidth: "100%",
        verticalAlign: "middle",
        ...(withBackground && {
          mt: 2,
          p: 1,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: 1,
        }),
        ...sx,
      }}
    >
      {showCopyButton && url && (
        <Tooltip
          title={copied ? "Copied!" : "Copy link"}
          placement="bottom"
          arrow
        >
          <IconButton
            size="small"
            color="primary"
            onClick={handleCopy}
            aria-label="Copy link"
            sx={{ flexShrink: 0 }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {showText && displayText && (
        <Typography
          variant="body1"
          title={displayText}
          sx={{
            minWidth: 0,
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayText}
        </Typography>
      )}

      {showQrDownloadButton && resolvedOpenUrl && (
        <Tooltip
          title={
            downloadingQr
              ? "Generating QR code..."
              : `Download QR code`
          }
          placement="bottom"
          arrow
        >
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={handleQrDownload}
              disabled={downloadingQr}
              aria-label={`Download ${openLabel} QR code`}
              sx={{ flexShrink: 0 }}
            >
              <QrCode2Icon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {showOpenButton && resolvedOpenUrl && (
        <Tooltip
          title={`Open link`}
          placement="bottom"
          arrow
        >
          <IconButton
            size="small"
            color="primary"
            component="a"
            href={resolvedOpenUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${openLabel} page`}
            sx={{ flexShrink: 0 }}
          >
            <LaunchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default LinkDisplay;